/**
 * Cross-step pending editor open from the Devices view (#999 Story 3.5 Phase 4b).
 *
 * Module-level bridge: Devices row tap sets pending { stepId, unitId }, navigates
 * to the step; the matching *Configurations subsection peeks until the unit is
 * present, then opens the editor and arms return-to-Devices. Closing the editor
 * navigates back to the location-view pseudo-step.
 *
 * Session is cleared on RequestDetail unmount / requestId change. Leaving an
 * equipment step for another non-Devices step abandons the return flag.
 */

import type { DeviceStepId } from './deviceRowMapper';

export interface DevicesPendingUnitOpen {
  stepId: DeviceStepId;
  unitId: number;
}

let pendingOpen: DevicesPendingUnitOpen | null = null;
let returnToDevices = false;
let returnHandler: (() => void) | null = null;

/** Coerce API/json unit ids (number or numeric string) for deep-link matching. */
export function coerceDevicesUnitId(id: unknown): number | null {
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  if (typeof id === 'string' && id.trim() !== '') {
    const parsed = Number(id);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

export const devicesEditorNavigation = {
  setPendingOpen(stepId: DeviceStepId, unitId: number): void {
    pendingOpen = { stepId, unitId };
    // Do not arm return until the editor actually opens (avoids stuck flag).
    returnToDevices = false;
  },

  peekPendingOpen(stepId: DeviceStepId): number | null {
    if (pendingOpen?.stepId !== stepId) {
      return null;
    }
    return pendingOpen.unitId;
  },

  hasPendingOpen(): boolean {
    return pendingOpen != null;
  },

  /**
   * Consume pending open and arm return-to-Devices. Call only when the editor
   * is about to open for this unit.
   */
  takePendingOpen(stepId: DeviceStepId): number | null {
    if (pendingOpen?.stepId !== stepId) {
      return null;
    }
    const unitId = pendingOpen.unitId;
    pendingOpen = null;
    returnToDevices = true;
    return unitId;
  },

  clearPendingOpen(): void {
    pendingOpen = null;
    returnToDevices = false;
  },

  /**
   * @deprecated Story 3.7 — no longer called from hooks (Strict Mode remount
   * cleared pending before units loaded). Kept for test compatibility.
   */
  clearOnStepUnmount(stepId: DeviceStepId): void {
    if (pendingOpen?.stepId === stepId) {
      pendingOpen = null;
    }
  },

  /**
   * Leave an equipment step without closing the editor / returning to Devices.
   * Also drops a pending open so a later visit cannot surprise-open a unit.
   */
  abandonReturnToDevices(): void {
    returnToDevices = false;
    pendingOpen = null;
  },

  /** Full session reset (request change / RequestDetail unmount). */
  resetSession(): void {
    pendingOpen = null;
    returnToDevices = false;
  },

  registerReturnHandler(handler: (() => void) | null): void {
    returnHandler = handler;
  },

  completeReturnToDevices(): void {
    if (!returnToDevices) {
      return;
    }
    returnToDevices = false;
    pendingOpen = null;
    returnHandler?.();
  },

  /** Test-only reset. */
  _resetForTests(): void {
    pendingOpen = null;
    returnToDevices = false;
    returnHandler = null;
  },
};
